<template>
  <!-- This component doesn't need hydration, so we don't pass any params -->
  <LazyHydrate>
    <StandardWrapper
      v-if="hasInfoToShow"
      v-bind="$attrs"
      :blok="blok"
      width="text"
    >
      <h2
        class="title"
        :class="
          textColorClass(blok.titleVariant, blok.bgVariant, 'text-darker')
        "
      >
        {{ blok.title }}
      </h2>
      <div v-if="blok.textContent" class="mt-6">
        <p
          class="paragraph prose prose-lg"
          :class="textColorClass(blok.textVariant, blok.bgVariant)"
        >
          {{ blok.textContent }}
        </p>
      </div>
      <div class="inline-flex flex-col gap-6 pt-9" :class="linkClass">
        <div v-if="websiteText" class="flex flex-row-reverse gap-3">
          <RichText
            class="peer w-full text-base"
            :blok="{
              ...blok,
              linkTextColor:
                blok.linkTextColor ||
                (isBackgroundDark(blok.bgVariant) ? 'white' : 'secondary'),
            }"
            :rich-text-content="blok.website"
          />
          <GlobeAltIcon
            class="h-6 min-h-6 w-6 min-w-6"
            :class="
              blok.linkTextColor
                ? textColorClass(blok.linkTextColor)
                : isBackgroundDark(blok.bgVariant)
                  ? 'text-white'
                  : 'text-secondary-500 peer-hover:text-secondary-600'
            "
          />
        </div>

        <a
          v-if="blok.contactEmail"
          class="inline-flex items-center text-base font-medium"
          :href="`mailto:${blok.contactEmail}`"
          :aria-label="`Email ${blok.contactEmail}`"
        >
          <EnvelopeIcon class="h-6 w-6" />
          <span class="ml-3">{{ blok.contactEmail }}</span>
        </a>

        <a
          v-if="blok.phone"
          class="inline-flex items-center text-base font-medium"
          :href="`tel:${blok.phone}`"
          :aria-label="`Telefoonnummer ${blok.phone}`"
        >
          <PhoneIcon class="h-6 w-6" />
          <span class="ml-3">{{ blok.phone }}</span>
        </a>
        <p
          v-if="blok.phone && blok.availability"
          class="-mt-5 ml-9 text-base"
          :class="textColorClass(blok.textVariant, blok.bgVariant)"
        >
          {{ blok.availability }}
        </p>
      </div>
      <div class="flex gap-3" :class="linkClass">
        <template v-for="channel in channels">
          <SocialMediaLink
            v-if="channel.url"
            :key="channel.name"
            class="mt-6"
            :channel="channel"
            :use-brand-color="!blok.useLinkColorInSocialLinks"
          />
        </template>
      </div>
    </StandardWrapper>
  </LazyHydrate>
</template>

<script setup>
import { computed } from 'vue'
import StandardWrapper from '../components/StandardWrapper.vue'
import SocialMediaLink from '../components/SocialMediaLink.vue'
import RichText from '../components/RichText.vue'
import { PhoneIcon, EnvelopeIcon, GlobeAltIcon } from '@heroicons/vue/20/solid'
import { renderRichText } from '@storyblok/vue'
import { useColorPalette } from '../composables/colorPalette'

const props = defineProps({
  blok: {
    type: Object,
    default: () => {},
  },
  context: {
    type: Object,
    default: () => {},
  },
})

const { textColorClass, isBackgroundDark, colorName } = useColorPalette()

const hasInfoToShow = computed(() => {
  const firstSocialLink = channels.value.find((channel) => !!channel.url)
  return (
    import.meta.env.VITE_IS_EDITOR ||
    !!firstSocialLink ||
    !!props.blok.contactEmail ||
    !!props.blok.phone ||
    !!websiteText.value
  )
})

const websiteText = computed(() => {
  if (props.blok.website) {
    if (typeof props.blok.website === 'string') {
      return props.blok.website
    }
    const renderedText = renderRichText(props.blok.website)
    return renderedText !== '<p></p>' &&
      renderedText !== `<p><a href="" target="_blank"></a></p>`
      ? renderedText
      : ''
  }

  return ''
})

const channels = computed(() => {
  return [
    {
      name: 'linkedin',
      url: props.blok.linkedinUrl || getCompanySocialLink('linkedIn'),
      label: 'LinkedIn',
    },
    {
      name: 'facebook',
      url: props.blok.facebookUrl || getCompanySocialLink('facebook'),
      label: 'Facebook',
    },
    {
      name: 'twitter',
      url: props.blok.twitterUrl || getCompanySocialLink('twitter'),
      label: 'Twitter',
    },
    {
      name: 'instagram',
      url: props.blok.instagramUrl || getCompanySocialLink('instagram'),
      label: 'Instagram',
    },
    {
      name: 'youtube',
      url: props.blok.youtubeUrl || getCompanySocialLink('youtube'),
      label: 'Youtube',
    },
    {
      name: 'tiktok',
      url: props.blok.tiktokUrl || getCompanySocialLink('tiktok'),
      label: 'Tiktok',
    },
  ]
})

const linkClass = computed(() => {
  if (props.blok.linkTextColor) {
    return `richtext-link ${colorName(props.blok.linkTextColor)}`
  }

  if (props.blok.textVariant) {
    return textColorClass(props.blok.textVariant)
  } else {
    return isBackgroundDark(props.blok.bgVariant)
      ? 'richtext-link white'
      : 'richtext-link secondary'
  }
})

const getCompanySocialLink = (platform) => {
  const link = props.context.company?.socialLinks?.find(
    (item) => item.type === platform,
  )
  return link?.url || ''
}
</script>

<style scoped>
:deep(a),
:deep(a > *) {
  text-decoration: none;
}
</style>
